import {Injectable} from "@angular/core";
import {VizixEventItem} from "../models/VizixEventItem";
import {UniqueDeviceID} from "@ionic-native/unique-device-id";
import {VizixClientService} from "vizixc/dist";
import {VizixService} from "./vizix.service";
import {SQLite, SQLiteObject} from "@ionic-native/sqlite";
import {Platform} from "ionic-angular";
import {Network} from "@ionic-native/network";
import {
  UUID_NOT_AVAILABLE, VIZIX_EVENT_DEPARTMENT, VIZIX_EVENT_FACILITY, VIZIX_EVENT_MESSAGE, VIZIX_EVENT_SYNC_ID,
  VIZIX_EVENT_TENANT_GROUP,
  VIZIX_EVENT_THING_TYPE_CODE, VIZIX_EVENT_ZONE_TYPE,
  VizixEventSource
} from "../app/app.constants";
import {Subscription} from "rxjs/Subscription";

@Injectable()
export class VizixEventService {

  private tag = '[EVENT_SERVICE]';
  private DB_NAME = 'VizixEvent.db';
  private DB_LOCATION = 'default';
  private DB_TABLE_VIZIX_EVENT = 'localVizixEvents';
  private db: SQLiteObject;
  private isConnected: boolean = false;
  private isCordova: boolean = false;
  private isCheckingPending: boolean = false;
  private TABLE_COLUMN_EVENT = 'event';
  private networkSubscriptionConnect: Subscription;
  private networkSubscriptionDisconnect: Subscription;

  constructor(public platform: Platform,
              private network: Network,
              private vizixService: VizixService,
              private uniqueDeviceID: UniqueDeviceID,
              private sqlite: SQLite,
              private vizixClientService: VizixClientService) {
  }

  /**
   * Initializes the database (if not exists) and starts the service
   */
  public init(skipUpload?: boolean) {
    console.log(this.tag, 'Initializing');

    // Init database and table if table not exists
    //  Events Table columns:
    //    event

    if (this.platform.is('cordova') && !this.platform.is('core')) {
      this.isCordova = true;
      console.log(this.tag, 'Platform is mobile, init db');

      this.sqlite.create({name: this.DB_NAME, location: this.DB_LOCATION}).then((db: SQLiteObject) => {
        this.db = db;
        this.db.executeSql('create table if not exists ' + this.DB_TABLE_VIZIX_EVENT + '(' + this.TABLE_COLUMN_EVENT + ' VARCHAR(100))', {})
          .then(() => {
            console.log(this.tag, 'Executed SQL');
            this.initService(skipUpload);
          })
          .catch(e => console.log(this.tag, e));
      }).catch(e => console.log(this.tag, e));
    } else {
      console.log(this.tag, 'Platform is not mobile, skipping database');
      this.isCordova = false;
      this.initService();
    }
  }

  public deleteDataBase() {
    if (this.isCordova) {
      this.sqlite.deleteDatabase({name: this.DB_NAME, location: this.DB_LOCATION}).then(() => {
        console.log(this.tag, 'Delete DB Events successful');
      }, err_ => {
        console.log(this.tag, 'ERROR Delete DB Events');
      });
    } else {
      console.log(this.tag, 'Platform is not cordova! Cant delete database');
    }
  }


  /**
   * Starts the service, checks for pending events to upload
   * It is aware of network connection
   */
  private initService(skipUpload?: boolean) {

    // Check if network connection is available
    if (this.network.type != 'none' && this.network.type != 'unknown') {
      // If available, then check if pending VizixEvents
      console.log(this.tag, 'network state: ' + this.network.type);
      this.isConnected = true;

      if (!skipUpload) {
        setTimeout(() => { // Wait 5 seconds on service startup to check if pending items (to init db)
          this.checkPending();
        }, 5000);
      }

    } else {
      console.log(this.tag, 'Not connected, waiting for network connection availability');
    }

    if (!this.networkSubscriptionConnect) {
      // Called when network changes from disconnected to connected
      this.networkSubscriptionConnect = this.network.onConnect().subscribe(() => {
        console.log(this.tag, 'network connected!');
        this.isConnected = true;
        this.checkPending();
      });
    }

    if (!this.networkSubscriptionDisconnect) {
      // Called when network changes from connected to disconnected
      this.networkSubscriptionDisconnect = this.network.onDisconnect().subscribe(() => {
        console.log(this.tag, 'network disconnected!');
        this.isConnected = false;
      });
    }
  }

  public recordEvent(event: VizixEventItem, bridgeCode?: string, thingTypeCode?: string): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      this.saveEvent(event, bridgeCode, thingTypeCode).then(() => {
        resolve(true);
      }, () => {
        resolve(true);
      });
    });
  }

  /**
   * Uploads VizixEventItem object to Vizix, if it fails, saves it locally
   * @param {VizixEventItem} vizixEventItem
   * @param bridgeCode in case thingsBridge is used
   * @param thingTypeCode in case thingsBridge is used
   */
  private saveEvent(vizixEventItem: VizixEventItem, bridgeCode?: string, thingTypeCode?: string): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      if (vizixEventItem.userGroup) {
        this.vizixService.saveToEvent(vizixEventItem, bridgeCode, thingTypeCode).then(() => {
          console.log(this.tag, 'Event recorded in Vizix');
          resolve(true);
        }, (error) => {
          if (!bridgeCode && !thingTypeCode) {
            console.log(this.tag, 'Error saving event in Vizix, saving locally');
            this.saveEventLocally(vizixEventItem);
          } else {
            console.log(this.tag, 'Not Things endpoint, skipping save');
          }
          resolve(true);
        });
      } else {
        if (!bridgeCode && !thingTypeCode) {
          console.log(this.tag, 'Error saving event in Vizix, saving locally');
          this.saveEventLocally(vizixEventItem);
        } else {
          console.log(this.tag, 'Not Things endpoint, skipping save');
        }
        resolve(true);
      }
    });
  }

  /**
   * Saves VizixEventItem object to database (as String)
   * @param {VizixEventItem} event
   */
  private saveEventLocally(event: VizixEventItem) {
    if (this.isCordova) {
      this.db.executeSql('INSERT INTO ' + this.DB_TABLE_VIZIX_EVENT + ' VALUES(?)', [JSON.stringify(event)])
        .then(res => {
          console.log(this.tag, 'Inserted', event);
        }).catch((error) => {
        console.log(this.tag, 'Error inserting', error);
      })
    } else {
      console.log(this.tag, 'Platform is not cordova! Cant save event locally');
    }
  }

  /**
   * Checks if there are events saved locally to upload
   */
  private checkPending() {
    if (!this.isCheckingPending) {
      this.isCheckingPending = true;
      if (this.isCordova) {
        if (this.isConnected) {
          console.log(this.tag, 'checking pending');
          // Try to upload event viewer information from db.
          // it is not necessary to group them, just upload
          this.db.executeSql('select * from ' + this.DB_TABLE_VIZIX_EVENT, {})
            .then(res => {
              console.log(this.tag, 'Events to upload: ' + res.rows.length);
              // Convert response from sql to list of events

              let eventsList: VizixEventItem[] = [];
              for (let i = 0; i < res.rows.length; i++) {
                console.log(this.tag, 'Processing event', res.rows.item(i));
                if (res.rows.item(i).event) {
                  eventsList.push(JSON.parse(res.rows.item(i).event));
                }
              }

              console.log(this.tag, 'Events list', eventsList);

              if (eventsList.length > 0) {
                for (let eventItem of eventsList) {
                  this.vizixService.saveToEvent(eventItem).then(() => {
                    this.removeEventFromDb(eventItem);
                    this.isCheckingPending = false;
                  }, (error) => {
                    console.log(this.tag, 'Error Uploading Event', error);
                    this.isCheckingPending = false;
                  });
                }
              } else {
                this.isCheckingPending = false;
                console.log(this.tag, 'No pending Events to upload');
              }

            }).catch(e => {
            this.isCheckingPending = false;
            console.log(this.tag, 'Error getting Events from database');
          });

        } else {
          this.isCheckingPending = false;
          console.log(this.tag, 'Not connected! waiting for internet connection');
        }
      } else {
        this.isCheckingPending = false;
        console.log(this.tag, 'Platform is not cordova! Cant check pending events');
      }
    } else {
      console.log(this.tag, 'Service is already checking pending events to upload');
    }
  }

  /**
   * Once uploaded successfully, the local event is removed from db
   * @param {VizixEventItem} event
   */
  private removeEventFromDb(event: VizixEventItem) {
    if (this.isCordova) {
      this.db.executeSql('delete from ' + this.DB_TABLE_VIZIX_EVENT +
        ' WHERE ' + this.TABLE_COLUMN_EVENT + ' = ?', [JSON.stringify(event)])
        .then(res => {
          console.log(this.tag, 'Removed event from db ', res);
        })
        .catch(e => {
          console.log(this.tag, e);
        });
    } else {
      console.log(this.tag, 'Platform is not cordova! Cant remove event');
    }
  }


  /**
   * Returns the basic data of the user, including the source, username, uuid, timestamps
   * Then the VizixEventItem object is modified depending on the workflow or category
   *
   * @returns {Promise<VizixEventItem>}
   * @param isError is used to generate an Error Message
   */
  public getVizixEventItemBase(isError?: boolean): Promise<VizixEventItem> {
    return new Promise<VizixEventItem>(resolve => {
      // Get the session ID, the UUID and the current username if no uuid provided
      this.uniqueDeviceID.get().then((newUuid: any) => {
        resolve(this.generateVizixEventItem(newUuid, isError));
      }, () => {
        console.log(this.tag, 'UUID not available');
        resolve(this.generateVizixEventItem(UUID_NOT_AVAILABLE, isError))
      })
        .catch((error: any) => {
          console.log(this.tag, 'Error getting UUID', error);
          resolve(this.generateVizixEventItem(UUID_NOT_AVAILABLE, isError));
        });
    });
  }

  /**
   * Generates a detailed message with thingtypecode and tenant values
   * @param {string} message the message that is included (can be error message)
   * @param {string} thingTypeCode
   * @param tenantGroup
   * @param department
   * @param zoneType
   * @returns {string} The stringified message
   */
  public generateDetailsMessage(message: any, thingTypeCode: string, tenantGroup: string, department?: string, zoneType?: string, syncSessionID?: number): string {
    let value = {};
    value[VIZIX_EVENT_TENANT_GROUP] = tenantGroup;
    value[VIZIX_EVENT_THING_TYPE_CODE] = thingTypeCode;
    value[VIZIX_EVENT_FACILITY] = this.vizixService.getStoreId();
    value[VIZIX_EVENT_MESSAGE] = message;
    if (department) {
      value[VIZIX_EVENT_DEPARTMENT] = department;
    }
    if (zoneType) {
      value[VIZIX_EVENT_ZONE_TYPE] = zoneType;
    }
    if (syncSessionID) {
      value[VIZIX_EVENT_SYNC_ID] = syncSessionID;
    }
    return JSON.stringify(value);
  }

  /**
   * Generates a VizixEventObject from a uuid
   * @param uuid
   * @param isError
   * @returns {VizixEventItem}
   */
  private generateVizixEventItem(uuid: any, isError: boolean): VizixEventItem {
    return new VizixEventItem().fromBaseVizixEvent(this.vizixService.vizixTime(),
      String(uuid),
      VizixEventSource.MOBILE,
      this.vizixClientService.$config.user,
      isError,
      this.vizixService.configurationApp ?
        this.vizixService.configurationApp.AppParametersConfiguration ?
          this.vizixService.configurationApp.AppParametersConfiguration.VisibilityGroupId.value : '' : '',
      '');
  }

  // Get the the UUID
  public GetUUID(): Promise<string> {
    return new Promise((resolve, reject) => {
      this.uniqueDeviceID.get().then((uuid: any) => resolve(uuid)).catch((error: any) => reject('web'));
    });
  }

}



// WEBPACK FOOTER //
// ./src/providers/vizix.event.service.ts